import { useSyncExternalStore } from 'react'
import { onlineManager } from '@tanstack/react-query'

/**
 * Признак сети для всего приложения (этап 4, офлайн PWA).
 *
 * «Есть сеть» = браузер считает себя в сети И сервер отвечает. Второе нужно
 * потому, что navigator.onLine врёт в зале: телефон подключён к Wi-Fi без
 * интернета или к сотовой сети без данных и считает себя «в сети». Поэтому
 * слой данных сообщает о сетевом сбое (reportNetworkFailure), а проверка
 * доступности (setReachabilityProbe, её задаёт PWA) подтверждает или снимает
 * подозрение. Пока сервер недоступен, проверка повторяется раз в 10 секунд и
 * при возврате в приложение.
 *
 * react-query следит за этим же признаком: без сети запросы чтения ставятся на
 * паузу (данные на экране остаются), а с появлением сети продолжаются сами.
 * Записи react-query не ведёт - они идут напрямую через SliceApi, и без сети
 * слой данных отказывает сразу (см. liveApi guard), ничего не копя.
 */

const PROBE_INTERVAL_MS = 10_000

/** true - сервер ответил; false или исключение - не ответил. */
export type ReachabilityProbe = () => Promise<boolean>

function browserSaysOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

let browserOnline = browserSaysOnline()
let reachable = true
let probe: ReachabilityProbe | null = null
let probing = false
let timer: ReturnType<typeof setTimeout> | undefined
let last = browserOnline && reachable
const listeners = new Set<() => void>()
const backOnline = new Set<() => void>()

export function isOnline(): boolean {
  return browserOnline && reachable
}

function emit() {
  const now = isOnline()
  if (now === last) return
  last = now
  for (const l of [...listeners]) l()
  if (!now) return
  for (const cb of [...backOnline]) {
    try {
      cb()
    } catch {
      // подписчик не должен ломать остальных
    }
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

async function runProbe() {
  if (!probe || probing) return
  probing = true
  let ok = false
  try {
    ok = await probe()
  } catch {
    ok = false
  } finally {
    probing = false
  }
  clearTimeout(timer)
  // Пока проверяли, браузер сам сказал «нет сети» или проверку сняли.
  if (!browserOnline || !probe) return
  reachable = ok
  emit()
  if (!ok) timer = setTimeout(() => void runProbe(), PROBE_INTERVAL_MS)
}

/** Хук: есть ли сеть. Перерисовывает компонент при смене. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, isOnline, () => true)
}

/** Подписка на появление сети (переход «нет» -> «есть»). Возвращает отписку. */
export function onOnline(cb: () => void): () => void {
  backOnline.add(cb)
  return () => {
    backOnline.delete(cb)
  }
}

/**
 * Запрос не дошёл до сервера (нет ответа). Без проверки доступности ничего не
 * делает: одного сбоя мало, чтобы объявить «нет сети».
 */
export function reportNetworkFailure(): void {
  if (!probe || !browserOnline || !reachable) return
  void runProbe()
}

/** Проверка доступности сервера; null - выключить (сервер считается доступным). */
export function setReachabilityProbe(p: ReachabilityProbe | null): void {
  probe = p
  if (!p) {
    clearTimeout(timer)
    reachable = true
    emit()
  }
}

/** Только для тестов: вернуть модуль в исходное состояние. */
export function resetNetworkForTests(): void {
  clearTimeout(timer)
  probe = null
  probing = false
  browserOnline = browserSaysOnline()
  reachable = true
  emit()
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    browserOnline = true
    // Оптимистично: следующий сбой запроса снова позовёт проверку.
    reachable = true
    clearTimeout(timer)
    emit()
  })
  window.addEventListener('offline', () => {
    browserOnline = false
    clearTimeout(timer)
    emit()
  })
  // Таймеры в свёрнутом приложении на iOS стоят: при возврате проверяем сразу.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && browserOnline && !reachable) void runProbe()
  })
  // react-query по умолчанию считает себя в сети при старте; берём наш признак.
  onlineManager.setEventListener((setOnline) => {
    const sync = () => setOnline(isOnline())
    listeners.add(sync)
    sync()
    return () => {
      listeners.delete(sync)
    }
  })
}
